import {
  getPageHeader,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionPrimary,
  PageHeaderActions,
  dashboardMetricGridClassName,
  VisibleFilterFields,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@yeshuman/sdk'
import { useState, useEffect, useRef } from 'react'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { Activity as ActivityIcon, Plus } from 'lucide-react'
import { Activity, useActivities, useCreateActivity, useUpdateActivity, useDeleteActivity, ActivityFilters, CreateActivityData, UpdateActivityData } from '../hooks/use-activities'
import { ActivityCard } from './activity-card'
import { ActivityFormDialog } from './activity-form-dialog'
import { ActivityStats } from './activity-stats'
import { getHealthActivityFilterDefinitions } from '../list-filters/activity'

const activityFilterDefinitions = getHealthActivityFilterDefinitions()
import { toast } from 'sonner'

const activityCardGridClassName = dashboardMetricGridClassName(5)

export function ActivityPage() {
  const [filters, setFilters] = useState<ActivityFilters>({})
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingActivity, setEditingActivity] = useState<any>(null)
  const [deletingActivityId, setDeletingActivityId] = useState<string | null>(null)
  
  // Track previous activities for agent update animation
  const [highlightedActivities, setHighlightedActivities] = useState<Set<string>>(new Set())
  const previousActivitiesRef = useRef<Activity[]>([])

  const { activities, isLoading, error, refetch } = useActivities(filters)
  const createActivity = useCreateActivity()
  const updateActivity = useUpdateActivity()
  const deleteActivity = useDeleteActivity()

  // Detect agent updates and highlight changed activities
  useEffect(() => {
    if (activities && previousActivitiesRef.current.length > 0) {
      const changedIds = new Set<string>()
      
      // Find new activities
      activities.forEach((activity) => {
        const prev = previousActivitiesRef.current.find((a) => a.id === activity.id)
        if (!prev) {
          changedIds.add(activity.id)
        } else {
          // Check if activity was updated (compare JSON strings)
          if (JSON.stringify(prev) !== JSON.stringify(activity)) {
            changedIds.add(activity.id)
          }
        }
      })
      
      // Check for deleted activities (not needed for highlighting, but good to know)
      
      if (changedIds.size > 0) {
        setHighlightedActivities(changedIds)
        // Fade out highlights after 3 seconds
        setTimeout(() => {
          setHighlightedActivities(new Set())
        }, 3000)
      }
    }
    
    // Update ref (doesn't trigger re-render)
    if (activities) {
      previousActivitiesRef.current = activities
    }
  }, [activities])

  const handleCreate = async (data: CreateActivityData) => {
    await createActivity.mutateAsync(data)
  }

  const handleUpdate = async (data: UpdateActivityData) => {
    await updateActivity.mutateAsync(data)
  }

  const handleDelete = async (activityId: string) => {
    try {
      await deleteActivity.mutateAsync(activityId)
      toast.success('Activity deleted successfully')
      setDeletingActivityId(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete activity')
    }
  }

  const handleEdit = (activity: any) => {
    setEditingActivity(activity)
    setIsFormOpen(true)
  }

  const handleAdd = () => {
    setEditingActivity(null)
    setIsFormOpen(true)
  }

  const handleFormClose = () => {
    setIsFormOpen(false)
    setEditingActivity(null)
  }

  // Group activities by date
  const groupActivitiesByDate = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const weekAgo = new Date(today)
    weekAgo.setDate(weekAgo.getDate() - 7)

    const groups = {
      today: [] as any[],
      yesterday: [] as any[],
      thisWeek: [] as any[],
      older: [] as any[],
    }

    activities.forEach((activity) => {
      const activityDate = new Date(activity.date)
      activityDate.setHours(0, 0, 0, 0)

      if (activityDate.getTime() === today.getTime()) {
        groups.today.push(activity)
      } else if (activityDate.getTime() === yesterday.getTime()) {
        groups.yesterday.push(activity)
      } else if (activityDate >= weekAgo) {
        groups.thisWeek.push(activity)
      } else {
        groups.older.push(activity)
      }
    })

    return groups
  }

  const groupedActivities = groupActivitiesByDate()

  const { title, description } = getPageHeader('/activity', 'Activity', 'Track your physical activity including exercise, sleep, and other wellness activities.')
  return (
    <PageContainer>
      <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<ActivityIcon className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary onClick={handleAdd}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Add Activity
                </PageHeaderActionPrimary>
              }
            />
          }
        />

        {/* Statistics */}
        <ActivityStats activities={activities} />

        {/* Filters */}
        <VisibleFilterFields
          listId="healthActivity"
          filters={filters}
          onFiltersChange={setFilters}
          definitions={activityFilterDefinitions}
          ctx={undefined}
        />

        {/* Error State */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">Failed to load activities: {error.message}</p>
              <div className="mt-4">
                <Button variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {isLoading ? <HealthListPageSkeleton showFilters={false} ariaLabel="Loading activities" /> : null}

        {/* Activities List */}
        {!isLoading && !error && (
          <div className="space-y-6">
            {activities.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <ActivityIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No activities yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Start tracking your health activities by adding your first activity.
                    </p>
                    <Button onClick={handleAdd}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Activity
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {groupedActivities.today.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Today</h2>
                    <div className={activityCardGridClassName}>
                      {groupedActivities.today.map((activity) => (
                        <ActivityCard
                          key={activity.id}
                          activity={activity}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingActivityId(id)}
                          isHighlighted={highlightedActivities.has(activity.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedActivities.yesterday.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Yesterday</h2>
                    <div className={activityCardGridClassName}>
                      {groupedActivities.yesterday.map((activity) => (
                        <ActivityCard
                          key={activity.id}
                          activity={activity}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingActivityId(id)}
                          isHighlighted={highlightedActivities.has(activity.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedActivities.thisWeek.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">This Week</h2>
                    <div className={activityCardGridClassName}>
                      {groupedActivities.thisWeek.map((activity) => (
                        <ActivityCard
                          key={activity.id}
                          activity={activity}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingActivityId(id)}
                          isHighlighted={highlightedActivities.has(activity.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedActivities.older.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Older</h2>
                    <div className={activityCardGridClassName}>
                      {groupedActivities.older.map((activity) => (
                        <ActivityCard
                          key={activity.id}
                          activity={activity}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingActivityId(id)}
                          isHighlighted={highlightedActivities.has(activity.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <ActivityFormDialog
          open={isFormOpen}
          onOpenChange={handleFormClose}
          activity={editingActivity}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingActivityId} onOpenChange={(open) => !open && setDeletingActivityId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete activity</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this activity? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deletingActivityId && handleDelete(deletingActivityId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
  )
}
